import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Calendar as CalendarIcon, Pencil } from "lucide-react";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Calendar } from "@/shared/components/ui/calendar";
import { usePutBatchMutation } from "@/services/backend/batches/batch/service";
import { GrowthMedium, SampleType } from "@/services/backend/types";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { growthMedia, sampleTypes } from "@/shared/components/enum-labels";
import { de } from "date-fns/locale";
import { formatDate } from "@/shared/utilities/date";
import { Batch } from "@/services/backend/batches/batch/batch";
import { useEffect, useState } from "react";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";

const formSchema = z.object({
  expiryDate: z.date({
    message: "Ein Ablaufdatum wird benötigt",
  }),
  batchNumber: z.string(),
  sampleType: z.string(),
  growthMedium: z.string(),
});

interface EditBatchDialogProps {
  batch: Batch;
}

export default function EditBatchDialog({ batch }: EditBatchDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [putBatch, { isLoading, isSuccess, error }] = usePutBatchMutation();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      batchNumber: batch.batchNumber,
      growthMedium: batch.growthMedium,
      sampleType: batch.sampleType,
      expiryDate: new Date(batch.expiryDate),
    },
  });

  useEffect(() => {
    function reset(): void {
      if (isSuccess) {
        form.reset();
        setIsOpen(false);
      }
    }

    reset();
  }, [form, isSuccess]);

  function onSubmit(values: z.infer<typeof formSchema>) {
    const selectedDate = values.expiryDate.toISOString();
    putBatch({
      batch: {
        id: batch.id,
        batchNumber: values.batchNumber,
        expiryDate: selectedDate,
        growthMedium: values.growthMedium as GrowthMedium,
        sampleType: values.sampleType as SampleType,
      },
    });
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Pencil />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Charge bearbeiten</DialogTitle>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <div className="flex items-center space-x-2">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="w-full space-y-8"
            >
              <FormField
                control={form.control}
                name="batchNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Chargennummer</FormLabel>
                    <FormControl>
                      <Input
                        disabled={isLoading}
                        placeholder="Chargennummer"
                        type="number"
                        min="1"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>Chargennummer</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="expiryDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Ablaufdatum</FormLabel>
                    <Popover
                      open={isDatePickerOpen}
                      onOpenChange={setIsDatePickerOpen}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          disabled={isLoading}
                          variant="outline"
                          data-empty={!field.value}
                          className="data-[empty=true]:text-muted-foreground justify-start text-left font-normal"
                        >
                          <CalendarIcon />
                          {field.value ? (
                            formatDate(field.value.toUTCString(), de)
                          ) : (
                            <span>Datum auswählen</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(date) => {
                            field.onChange(date);
                            setIsDatePickerOpen(false);
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="growthMedium"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nährmedium</FormLabel>
                    <Select
                      disabled={isLoading}
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Bitte Nährmedium auswählen." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.values(GrowthMedium).map((value) => (
                          <SelectItem key={value} value={value}>
                            {growthMedia[value]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>Nährmedium der Charge</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="sampleType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Probentyp</FormLabel>
                    <Select
                      disabled={isLoading}
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Bitte Probentyp auswählen" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.values(SampleType).map((value) => (
                          <SelectItem key={value} value={value}>
                            {sampleTypes[value]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>Der Probentyp der Charge</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button disabled={isLoading} type="submit">
                Speichern
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
